import axios, { AxiosRequestConfig } from "axios";
import { NewProjectConfig, ProjectConfig } from "providers/HostedScrapingProvider";
import { CostCalculationProject } from "providers/HostedScrapingProvider/types";
import { ApiCallConfig } from "routes/dashboard/ApiPlaygroundTypes";
import { CaptchaTokenType } from "types/Captcha";
import { AutoRenewalSetting } from "types/billing/AutoRenewalSetting";
import { PaymentSource, PaymentSourceRequest } from "types/billing/PaymentSource";
import {
  AnalyticsDomainResponseItem,
  AnalyticsOverviewResponse,
  DomainDetailsRow,
  DomainSearchResult,
  ErrorLogsResponse,
  FilterOptionsResponse,
  SeverityLevel,
} from "utils/analytics/types";
import { Coupon } from "types/Coupons";
import ActiveSubscription from "types/ActiveSubscription";
import { Issue, StatuspageIssues } from "routes/dataroutes/IssuesData";
import { PaginatedInvoicesResponse } from "v2/pages/billing/types";
import { Invoice } from "v2/hooks/billing/useInvoices";
import { TLDs } from "hooks/useTLDs";
import { PricingOptions } from "types/billing/pricing-options/PricingOptions";
import Plan from "types/Plan";
import { LegacyPaymentSource, normalizePaymentSources } from "utils/normalizePaymentSources";


// This is the scraperapi-server's address, not the scraperapi-api's
export const API_HOST = process.env.REACT_APP_API_URL;


export interface ApiErrorResponse {
  message: string | undefined;
  error_code: string; // TODO this is kind of an enumeration in the backend services, maybe we should list the possible values here?
  details?: string | object;
}


export interface ChargebeeApiErrorResponse extends ApiErrorResponse {
  chargebee_api_error_code: string;
  chargebee_error_code: string;
}

interface SubscriptionEstimateResponse {
  invoiceEstimate?: {
    subTotal: number;
    total: number;
    amountDue: number;
    currencyCode: string;
    discounts?: Array<{ amount: number; description?: string; entityId?: string }>;
  };
}

export type JobOrder = undefined | 'job_completed_first' | 'job_failed_first' | 'last_run_first' | 'last_run_last';

export interface LogoutType {
  logoutAllDevices?: Boolean;
}

export class ApiError<T extends ApiErrorResponse> extends Error implements ApiErrorResponse {

  constructor(errorResponse: T) {
    super(errorResponse.message);

    this.error_code = errorResponse.error_code;
    this.details = errorResponse.details;
  }

  static from(error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response?.data) {
        const jsonResponse = ((responseData) => {
          if (typeof responseData === 'object') {
            return responseData;
          }
          if (typeof responseData === 'string') {
            try {
              return JSON.parse(responseData);
            } catch (err) {
              return responseData;
            }
          }
        })(error.response.data);

        if (typeof jsonResponse === 'object') {
          if (('message' in jsonResponse) && ('error_code' in jsonResponse)) {
            return new ApiError(jsonResponse);
          }

          return new ApiError({
            message: jsonResponse.message,
            error_code: 'unknown',
            details: jsonResponse
          });
        }

        if (typeof jsonResponse === 'string') {
          return new ApiError({
            message: jsonResponse,
            error_code: 'unknown'
          });
        }
      }
    }

    return undefined;
  }

  error_code: string;
  details?: any;

}

async function scraperFetch<T = any>(path: string, opts?: AxiosRequestConfig): Promise<T | null> {
  if (path[0] !== "/") throw Error(`Wrongly formatted path: ${path}`);

  try {
    const res = await axios(`${API_HOST}${path}`, {
      withCredentials: true,
      ...opts
    });

    return res.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      // expected, simply re-throw without any processing, so the framework can handle it
      throw error;
    }
    const apiError = ApiError.from(error);

    throw apiError || error;
  }
}

const scraperApi = {
  auth: {
    check: async (opts?: AxiosRequestConfig) => scraperFetch("/auth", opts),
    me: async (opts?: AxiosRequestConfig) => scraperFetch("/users", opts),
    github: {
      state: async () => scraperFetch("/oauth/github/state")
    },
    changePassword: async (oldPassword: string, newPassword: string) =>
      scraperFetch(`/users/password`, {
        method: "POST",
        data: {
          password: oldPassword,
          newPassword: newPassword
        }
      }),
    google: {
      signin: async (token: string) =>
        scraperFetch("/users/sign-in/google", {
          method: "POST",
          data: { token }
        }),
      signup: async (token: string) =>
        scraperFetch("/users/sign-up/google", {
          method: "POST",
          data: { token }
        })
    },
    coupons: async (couponCode: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/users/coupons?v2`, {
        ...opts,
        method: "POST",
        data: {
          coupons: [ couponCode ],
          action: "apply"
        }
      }),
    sendActivationEmail: async (email: string) =>
      scraperFetch("/users/sendActivationEmail", {
        method: "POST",
        data: { email }
      }),

    activateAccount: async (token: string, opts?: AxiosRequestConfig) =>
      scraperFetch("/users/activate", { ...opts, params: { ...opts?.params, token } }),

    resetPassword: async (password: string, token: string) =>
      scraperFetch("/users/reset", { method: "POST", data: { password }, params: { token } }),

    forgotPassword: (email: string) =>
      scraperFetch("/users/request-reset", {
        method: "POST",
        data: { email }
      }),
    logout: async (data?: LogoutType) => scraperFetch("/users/logout", { method: "POST", data }),
    login: async (
      formData: {
        email: string,
        password: string,
        token: string,
        tokenType: CaptchaTokenType,
        firstPage?: string | { url: string; visited: Date },
        referer?: string
      }) =>
      scraperFetch("/users/sign-in/email", {
        method: "POST",
        data: formData
      }),
    signup: async (
      formData: {
        email: string,
        password: string,
        token: string,
        tokenType: CaptchaTokenType,
        firstPage?: string | { url: string; visited: Date } | undefined,
        referer?: string
      }
    ) =>
      scraperFetch("/users/sign-up/email", {
        method: "POST",
        data: formData
      }),
    requestDeletion: async (opts?: AxiosRequestConfig) => scraperFetch("/users/request-deletion", { ...opts, method: "POST" }),
  },
  apiKeys: {
    fetchApiKeys: async (opts?: AxiosRequestConfig) => scraperFetch("/users/apiKeys", opts),
    newApiKey: async (opts?: AxiosRequestConfig) => scraperFetch("/users/apiKey", { ...opts, method: "PUT" }),
    removeApiKey: async (apiKey: string, opts?: AxiosRequestConfig) => scraperFetch(`/users/apiKeys/${apiKey}`, { ...opts, method: "DELETE" })
  },
  status: {
    issues: async (opts?: AxiosRequestConfig) =>
      scraperFetch<StatuspageIssues>("/issues/statuspage/active", opts),
    allIssues: async (opts?: AxiosRequestConfig) =>
      scraperFetch<Issue[]>("/issues/active", opts),
    changelog: async (limit: number, opts?: AxiosRequestConfig) =>
      scraperFetch("/changelog", { ...opts, params: { limit: limit } }),
  },
  billing: {
    paymentSources: {
      getPaymentSources: async (opts?: AxiosRequestConfig) =>
        scraperFetch<Array<PaymentSource | LegacyPaymentSource>>("/subscriptions/payment-sources", opts).then(normalizePaymentSources),
      addOrUpdatePaymentSource: async (paymentSourceRequest: PaymentSourceRequest, opts?: AxiosRequestConfig) =>
        scraperFetch<Array<PaymentSource | LegacyPaymentSource>>("/subscriptions/payment-source", { ...opts, method: "POST", data: paymentSourceRequest }).then(normalizePaymentSources),
      createCheckoutSession: async (opts?: AxiosRequestConfig) =>
        scraperFetch<{ provider: "stripe", client_secret: string, publishable_key: string; api_version?: string }>("/subscriptions/payment-source/checkout-session", { ...opts, method: "POST" }),
      updateCheckoutSession: async (checkoutSessionId: string, metadata: Record<string, string>, opts?: AxiosRequestConfig) =>
        scraperFetch<void>(`/subscriptions/payment-source/checkout-session/${checkoutSessionId}/update`, { ...opts, method: "PATCH", data: { metadata } }),
      completeCheckoutSession: async (checkoutSessionId: string, opts?: AxiosRequestConfig) =>
        scraperFetch<void>(`/subscriptions/payment-source/checkout-session/${checkoutSessionId}/complete`, { ...opts, method: "POST" }),
      deletePaymentSource: async (paymentSourceId: string, opts?: AxiosRequestConfig) =>
        scraperFetch<Array<PaymentSource | LegacyPaymentSource>>(`/subscriptions/payment-sources/${paymentSourceId}`, { ...opts, method: "DELETE" }).then(normalizePaymentSources),
      addPayPalPaymentSource: async (billingAgreementToken: string, opts?: AxiosRequestConfig) =>
        scraperFetch<Array<PaymentSource | LegacyPaymentSource>>("/subscriptions/payment-source/paypal", { ...opts, method: "POST", data: { billing_agreement_token: billingAgreementToken } }).then(normalizePaymentSources),
    },
    getBillingAddress: async (opts?: AxiosRequestConfig) =>
      scraperFetch("/subscriptions/billing-address", opts),
    setBillingAddress: async (billingAddress: any /* TODO set proper type here */, opts?: AxiosRequestConfig) =>
      scraperFetch("/subscriptions/billing-address", { ...opts, method: "POST", data: billingAddress }),
    // Payment source management (delete and set primary)
    setPrimaryPaymentSource: async (paymentSourceId: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/subscriptions/payment-sources/${paymentSourceId}/set-primary`, { ...opts, method: "POST" }),
    payPal: {
      createBillingAgreementToken: async (opts?: AxiosRequestConfig) =>
        scraperFetch("/billing/paypal/billing-agreement/create-token", opts),
    },
    invoices: {
      getUnpaidInvoicesCount: async (opts?: AxiosRequestConfig) =>
        scraperFetch("/users/invoices/not-paid/count", opts),
      getInvoices: async (opts?: AxiosRequestConfig) =>
        scraperFetch<Invoice[]>("/users/invoices/paid", opts),
      getInvoicePdf: async (invoiceId: string, opts?: AxiosRequestConfig) =>
        scraperFetch(`/users/invoices/${invoiceId}/pdf`, opts),
      getInvoicesPaginated: async (params?: { page?: number; pageSize?: number; status?: string }, opts?: AxiosRequestConfig) =>
        scraperFetch<PaginatedInvoicesResponse>("/users/invoices/v2", { ...opts, params: { ...opts?.params, ...params } }),
    },
    getPlans: async (opts?: AxiosRequestConfig) =>
      scraperFetch<Plan[][]>("/v2/plans", opts),
    coupons: {
      getActiveCoupons: async (opts?: AxiosRequestConfig) =>
        scraperFetch<Coupon[]>("/coupons", opts),
      checkCoupons: async (couponCodes: string[], targetPlanSlug?: string, opts?: AxiosRequestConfig) =>
        scraperFetch<Coupon[]>("/coupons?v2", {
          ...opts,
          method: "POST",
          data: {
            coupons: couponCodes,
            targetPlanSlug,
            action: "check"
          }
        }),
      addCoupons: async (couponCodes: string[], targetPlanSlug?: string, opts?: AxiosRequestConfig) =>
        scraperFetch<Coupon[]>("/coupons?v2", {
          ...opts,
          method: "POST",
          data: {
            coupons: couponCodes,
            targetPlanSlug,
            action: "apply"
          }
        }),
      deleteCoupon: async (couponCode: string, opts?: AxiosRequestConfig) =>
        scraperFetch<Coupon[]>(`/coupons/${encodeURIComponent(couponCode)}`, {
          ...opts,
          method: "DELETE",
        }),
    },
  },
  subscription: {
    update: async (
      planSlug: string,
      coupons?: string[],
      keepScheduledChanges?: boolean,
      opts?: AxiosRequestConfig
    ) =>
      scraperFetch<ActiveSubscription>("/subscriptions", {
        ...opts,
        method: "POST",
        data: {
          plan: planSlug,
          coupons,
          keepScheduledChanges
        }
      }),
    autoRenew: async (ceilingPercentage: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/users/auto-renewal/${ceilingPercentage}`, {
        ...opts,
        method: "PUT"
      }),
    clearAutoRenew: async (opts?: AxiosRequestConfig) =>
      scraperFetch(`/users/auto-renewal/clear`, { ...opts, method: "PUT" }),
    changeAutoRenewal: async (autoRenewalSetting: AutoRenewalSetting, opts?: AxiosRequestConfig) =>
      scraperFetch(`/users/auto-renewal/${autoRenewalSetting}`, { ...opts, method: "PUT" }),
    renew: async (
      subscriptionId: string,
      coupons?: string[],
      keepScheduledChanges?: boolean,
      opts?: AxiosRequestConfig
    ) =>
      scraperFetch(`/subscriptions/${subscriptionId}/renewalDate`, {
        ...opts,
        method: "POST",
        data: {
          keepScheduledChanges,
          coupons
        }
      }),
    paymentMethod: async (opts?: AxiosRequestConfig) =>
      scraperFetch("/subscriptions/payment-method", { ...opts }),
    allPaymentMethods: async (opts?: AxiosRequestConfig) => {
      const statuses = [ "valid", "expiring", "pending_verification", "expired", "invalid" ];
      const statusParams = new URLSearchParams(statuses.map(s => [ "status", s]));
      const [ paymentMethod, virtualBankAccount ] = await Promise.all([
        scraperFetch("/subscriptions/payment-method-filtered", { params: statusParams, ...opts }),
        scraperFetch("/users/virtual-bank-account", { ...opts })
      ]);

      return {
        paymentMethod: paymentMethod?.id ? paymentMethod : undefined,
        virtualBankAccount: virtualBankAccount?.id ? virtualBankAccount : undefined
      };
    },
    billingSessions: async () => scraperFetch("/subscriptions/billing-session"),
    payNowLink: async () => scraperFetch("/subscriptions/pay-now-link"),

    cancel: async (opts?: AxiosRequestConfig) =>
      scraperFetch<ActiveSubscription>(`/subscriptions?v2`, {
        ...opts,
        method: "DELETE"
      }),
    removeScheduledChanges: async (opts?: AxiosRequestConfig) =>
      scraperFetch<ActiveSubscription>(`/subscriptions/scheduled-changes`, {
        ...opts,
        method: "DELETE"
      }),
    active: async (opts?: AxiosRequestConfig) => scraperFetch<ActiveSubscription>("/subscriptions/active", opts),
    estimate: async (planId: string, couponIds?: string[], endOfTerm?: boolean, opts?: AxiosRequestConfig) =>
      scraperFetch<SubscriptionEstimateResponse>("/subscriptions/estimate", {
        ...opts,
        params: {
          planId,
          couponIds: couponIds?.join(','),
          endOfTerm: endOfTerm ? 'true' : undefined,
        }
      }),
    toggleAutoUpgrade: async (autoUpgrade: boolean, opts?: AxiosRequestConfig) =>
      scraperFetch<PricingOptions>("/users/auto-upgrade", {
        ...opts,
        method: "POST",
        data: {
          autoUpgrade: autoUpgrade ? "on" : "off"
        }
      }),
    togglePayAsYouGo: async (payAsYouGo: boolean, creditsCap: number, contactSales: boolean, opts?: AxiosRequestConfig) =>
      scraperFetch<PricingOptions>("/users/pay-as-you-go", {
        ...opts,
        method: "POST",
        data: {
          payAsYouGo: payAsYouGo ? "on" : "off",
          creditsCap: creditsCap,
          contactSales: contactSales
        }
      }),
    getPricingOptions: async (opts?: AxiosRequestConfig) =>
      scraperFetch<PricingOptions>("/users/pricing-options", opts),
  },
  analytics: {
    getOverviewData: async (params: {
      startDate?: string;
      endDate?: string;
      domains?: string[];
      sourceProduct?: string;
      structuredDataEndpoint?: string;
      countries?: string[];
      render?: boolean;
      premium?: boolean;
      ultraPremium?: boolean;
    }, opts?: AxiosRequestConfig) => scraperFetch<{ data: AnalyticsOverviewResponse }>(`/users/analytics/overview`, { ...opts, method: 'POST', data: params }),
    getDomainList: async (params: {
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
      domains?: string[];
      sourceProduct?: string;
      structuredDataEndpoint?: string;
      countries?: string[];
      render?: boolean;
      premium?: boolean;
      ultraPremium?: boolean;
    }, opts?: AxiosRequestConfig) => scraperFetch<{ data: AnalyticsDomainResponseItem[] }>(`/users/analytics/domains`, { ...opts, method: 'POST', data: params }),
    getDomainDetails: async (params: {
      domain: string;
      startDate?: string;
      endDate?: string;
      sourceProduct?: string;
      structuredDataEndpoint?: string;
      countries?: string[];
      render?: boolean;
      premium?: boolean;
      ultraPremium?: boolean;
    }, opts?: AxiosRequestConfig) => scraperFetch<{ data: DomainDetailsRow[] }>(`/users/analytics/domain-details`, { ...opts, method: 'POST', data: params }),
    getFilterOptions: async (params: {
      startDate?: string;
      endDate?: string;
      sourceProduct?: string;
      structuredDataEndpoint?: string;
      render?: boolean;
      premium?: boolean;
      ultraPremium?: boolean;
      selectedDomains?: string[];
      selectedCountries?: string[];
    }, opts?: AxiosRequestConfig) => scraperFetch<{ data: FilterOptionsResponse }>(`/users/analytics/filter-options`, { ...opts, method: 'POST', data: params }),
    getErrorLogs: async (params: {
      startDate?: string;
      endDate?: string;
      page?: number;
      pageSize?: number;
      searchTerm?: string;
      severity?: SeverityLevel | null;
      statusCodes?: number[];
      domains?: string[];
    }, opts?: AxiosRequestConfig) => scraperFetch<{ data: ErrorLogsResponse }>(`/users/analytics/error-logs`, { ...opts, method: 'POST', data: params }),
    downloadDomainData: async (params: {
      startDate?: string;
      endDate?: string;
      domains?: string[];
      sourceProduct?: string;
      structuredDataEndpoint?: string;
      countries?: string[];
      render?: boolean;
      premium?: boolean;
      ultraPremium?: boolean;
    }) =>
      scraperFetch<Blob>(`/users/analytics/domains/export`, {
        method: 'POST',
        data: params,
        responseType: "blob",
      }),
    downloadErrorLogs: async (params: {
      startDate?: string;
      endDate?: string;
      domains?: string[];
      sourceProduct?: string;
      structuredDataEndpoint?: string;
      countries?: string[];
      render?: boolean;
      premium?: boolean;
      ultraPremium?: boolean;
      statusCodes?: number[];
      searchTerm?: string;
      severity?: SeverityLevel | null;
      status?: string;
    }) =>
      scraperFetch<Blob>(`/users/analytics/error-logs/export`, {
        method: 'POST',
        data: params,
        responseType: "blob",
      }),
    searchDomains: async (params: {
      startDate?: string;
      endDate?: string;
      search: string;
      limit?: number;
    }, opts?: AxiosRequestConfig) => scraperFetch<{ data: DomainSearchResult[] }>(`/users/analytics/domain-search`, { ...opts, method: 'POST', data: params }),
    },
    stats: {
      perDomainReport: async (query?: { source: "clickhouse" | "redis" }) =>
        scraperFetch("/users/report?" + new URLSearchParams(query)),
      usageHistory: async (filters: URLSearchParams, opts?: AxiosRequestConfig) =>
        scraperFetch("/users/usage-history", { ...opts, params: filters }),
      counters: async (opts?: AxiosRequestConfig) =>
        scraperFetch("/users/counters", opts),
    },
  crawlers: {
    getAllCrawlers: async (params: {}, opts?: AxiosRequestConfig) => scraperFetch('/users/crawlers', { ...opts, params }),
    getCrawlerById: async (crawlerId: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/users/crawlers/${encodeURIComponent(crawlerId)}`, opts),
    createCrawler: async (crawlerData: any, opts?: AxiosRequestConfig) =>
      scraperFetch('/users/crawlers', { ...opts, method: 'POST', data: crawlerData }),
    updateCrawler: async (crawlerId: string, crawlerData: any, opts?: AxiosRequestConfig) =>
      scraperFetch(`/users/crawlers/${encodeURIComponent(crawlerId)}`, { ...opts, method: 'PUT', data: crawlerData }),
    getCrawlerJobs: async (crawlerId: string, params: { page?: number; limit?: number } = {}, opts?: AxiosRequestConfig) =>
      scraperFetch(`/users/crawlers/${encodeURIComponent(crawlerId)}/jobs`, { ...opts, params }),
    getCrawlerJobSummary: async (
      jobId: string,
      params?: { page?: number; limit?: number; search?: string; status?: string },
      opts?: AxiosRequestConfig
    ) =>
      scraperFetch(`/jobs/${encodeURIComponent(jobId)}/summary`, { ...opts, params }),
    getCrawlerResultsByKey: async (resultKey: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/results/${encodeURIComponent(resultKey)}`, opts),
    downloadCrawlerJobDataStream: async (jobId: string, format?: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/jobs/${encodeURIComponent(jobId)}/download`, {
        ...opts,
        params: {
          ...(opts?.params || {}),
          ...(format && { format: format.toLowerCase() })
        },
        responseType: 'blob', // Handle streaming data as blob for file download
      }),
    downloadAllCrawlerJobResults: async (jobId: string, format?: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/jobs/${encodeURIComponent(jobId)}/download-all`, {
        ...opts,
        params: {
          ...(opts?.params || {}),
          ...(format && { format: format.toLowerCase() })
        },
        responseType: 'blob', // Handle streaming data as blob for ZIP download
      }),
    cancelCrawlerJob: async (jobId: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/jobs/${encodeURIComponent(jobId)}/cancel`, { ...opts, method: 'DELETE' }),
    testCallback: async (callbackUrl: string, opts?: AxiosRequestConfig) =>
      scraperFetch('/crawlers/test-callback', { ...opts, method: 'POST', data: { callbackUrl } })
  },
  hostedScraping: {
    projects: async (limit?: number, offset?: number, orderBy?: string, opts?: AxiosRequestConfig) => {
      const queryParams = new URLSearchParams();
      if (limit !== undefined) {
        queryParams.append('limit', limit.toString());
      }
      if (offset !== undefined) {
        queryParams.append('offset', offset.toString());
      }
      if (orderBy !== undefined) {
        queryParams.append('orderBy', orderBy);
      }
      const query = queryParams.toString();
      return scraperFetch(`/hostedscraping/projects${query ? `?${query}` : ''}`, opts);
    },
    singleProject: async (projectId: number, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/${encodeURIComponent(projectId)}`, opts),
    demoProject: async (opts?: AxiosRequestConfig) => {
      const response = await scraperFetch(`/hostedscraping/projects`, opts);
      // Handle both old format (plain array) and new format ({ projects, totalCount })
      const allProjects: any[] = Array.isArray(response) ? response : response.projects;
      return allProjects.find((p: any) => p.demoProject) ?? null;
    },
    jobs: async (projectId: number, limit: number = 10, orderBy: JobOrder, opts?: AxiosRequestConfig) => {
      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit.toString());
      if (orderBy !== undefined) {
        queryParams.append('orderBy', orderBy);
      }
      return scraperFetch(`/hostedscraping/project/${projectId}/jobs?${queryParams.toString()}`, opts);
    },
    subtasks: async (projectId: number, jobId: number, limit: number = 50, offset: number = 0, status?: string, search?: string, opts?: AxiosRequestConfig) => {
      const queryParams = new URLSearchParams();
      queryParams.append('limit', limit.toString());
      queryParams.append('offset', offset.toString());
      if (status !== undefined) {
        queryParams.append('status', status);
      }
      if (search) {
        queryParams.append('search', search);
      }
      return scraperFetch(`/hostedscraping/project/${projectId}/job/${jobId}/subtasks?${queryParams.toString()}`, opts);
    },
    subtaskResult: async (projectId: number, jobId: number, subtaskId: number, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/${projectId}/job/${jobId}/subtasks/${subtaskId}/result`, opts),
    cancelSubtask: async (projectId: number, jobId: number, subtaskId: number, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/${projectId}/job/${jobId}/subtasks/${subtaskId}/cancel`, { ...opts, method: "POST" }),
    saveProject: async (project: NewProjectConfig, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/`, {
        ...opts,
        method: "POST",
        data: project,
      }),
    updateProject: async (project: ProjectConfig, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/${project.id}`, {
        ...opts,
        method: "PATCH",
        data: project,
      }),
    scheduleNowProject: async (projectId: number, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/${projectId}/schedulenow`, {
        ...opts,
        method: "POST",
      }),
    resendJobResult: async (projectId: number, jobId: number, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/${projectId}/job/${jobId}/resendresult`, {
        ...opts,
        method: "POST",
      }),
    downloadProjectInput: async(inputKey: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/input?inputKey=${encodeURIComponent(inputKey)}`, {
        ...opts,
        // Prevent JSON decode:
        transformResponse: (response) => (response),
        method: "GET",
      }),
    getRunningProjectInfo: async(opts?: AxiosRequestConfig) =>
      scraperFetch('/hostedscraping/runningandfuturejobs', {
        ...opts,
        method: "GET",
      }),
    uploadProjectInput: async(body: unknown, projectType: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/input`, {
        ...opts,
        method: "POST",
        data: body,
        params: { projecttype: projectType },
        headers: {
          'content-type': 'application/octet-stream',
        }
      }),
    tryIt: async (project: ApiCallConfig, opts?: AxiosRequestConfig) =>
      scraperFetch("/hostedscraping/tryit", {
        ...opts,
        method: "POST",
        data: project
      }),
    projectCost: async (project: CostCalculationProject, sourceProduct: 'api' | 'api_playground' | 'async' | 'hosted', opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/cost?source_product=${sourceProduct}`, {
        ...opts,
        method: "POST",
        data: project,
      }),
    errorReport: async (publicJobId: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/job/${publicJobId}/errorreport`, {
        ...opts,
        method: "GET",
      }),
    postponeJob: async (projectId: number, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/${projectId}/postpone`, {
        ...opts,
        method: "POST",
      }),
    cancelJob: async (projectId: number, jobId: number, opts?: AxiosRequestConfig) =>
      scraperFetch(`/hostedscraping/project/${projectId}/job/${jobId}`, {
        ...opts,
        method: "DELETE",
      }),
  },
  settings: {
    availableCountryCodes: async (opts?: AxiosRequestConfig) =>
      scraperFetch<string[]>("/geo", opts),
    availableTLDs: async (opts?: AxiosRequestConfig) =>
      scraperFetch<TLDs>("/tlds", opts),
  },
  cancellationSurvey: {
    saveAnswers: async (formDataJson: any) => {
      return scraperFetch("/users/cancellation-survey", {
        method: "POST",
        data: formDataJson,
      });
    }
  },
  chatbase: {
    identityToken: async (opts?: AxiosRequestConfig) =>
      scraperFetch("/chatbase/identity-token", opts)
  },
  multipliers: {
    getDomainMultiplier: async (domain: string, opts?: AxiosRequestConfig) =>
      scraperFetch(`/multiplier/${encodeURIComponent(domain)}`, opts),
  },
  v2: {
    subscription: {
      // Update Subscription
      update: async (customerId: string, updateRequest: any, opts?: AxiosRequestConfig) =>
        scraperFetch(`/v2/customers/${customerId}/subscription`, { ...opts, method: "POST", data: updateRequest }),
    }
  }
};

export default scraperApi;
